import React, { useContext } from 'react';

import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

import { SellFormContext } from '../../SellFormContext';
import { buildPackageEntry } from '../../../../discovery/shipping_labels/shipping_label_package';
import CarrierCalculatedPackageForm, { packageErrorKey } from './CarrierCalculatedPackageForm';

interface IProps {
  packageEntry: core_apimessages_ShippingLabelPackage;
  setPackageEntry: (core_apimessages_ShippingLabelPackage) => void;
  setIsInCarrierCalculatedMode: (boolean) => void;
  disabled?: boolean;
}

export default function CarrierCalculatedPackageFormContainer({
  packageEntry,
  setPackageEntry,
  setIsInCarrierCalculatedMode,
  disabled = false,
}: IProps) {
  const { currentCanonicalProductId, listingState } = useContext(SellFormContext);
  const inSuggestionsExperiment = useExpEnabled(experiments.PACKAGE_SIZE_SUGGESTIONS);
  const user = useUser();

  function onPackageSuggestionResponse(respData) {
    const suggestionData = respData?.canonicalproduct?.packageSizeSuggestion;
    const validSuggestionData = !!suggestionData && !packageErrorKey(suggestionData);
    const currentPackageEntryIsBlank = ['length', 'width', 'height', 'weight'].every((dimensionKey) => !packageEntry[dimensionKey].value);
    const suggestionDiffersFromCurrentEntry = validSuggestionData && ['length', 'width', 'height', 'weight'].some((dimensionKey) => {
      return Number(suggestionData[dimensionKey]?.value) !== Number(packageEntry[dimensionKey]?.value) ||
        suggestionData[dimensionKey]?.unit !== packageEntry[dimensionKey]?.unit;
    });

    const shouldUseSuggestion = validSuggestionData &&
      (currentPackageEntryIsBlank || packageEntry.reverbSuggested) &&
      suggestionDiffersFromCurrentEntry;

    if (shouldUseSuggestion) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: 'PackageSizeSuggestionAlert',
        experiments: formatExperimentsForEventAttributes(user, [experiments.PACKAGE_SIZE_SUGGESTIONS]),
      });

      if (inSuggestionsExperiment) {
        setPackageEntry(buildPackageEntry(suggestionData, undefined, true));
      }
    }
  }

  const { loading } = useQuery(
    FETCH_PACKAGE_SIZE_SUGGESTION,
    {
      skip: !currentCanonicalProductId || listingState !== 'draft',
      variables: { canonicalProductId: currentCanonicalProductId },
      onCompleted: onPackageSuggestionResponse,
    },
  );

  return (
    <CarrierCalculatedPackageForm
      packageEntry={packageEntry}
      setPackageEntry={setPackageEntry}
      setIsInCarrierCalculatedMode={setIsInCarrierCalculatedMode}
      disabled={disabled || loading}
    />
  );
}

export const FETCH_PACKAGE_SIZE_SUGGESTION = gql(`
  query Core_SellForm_FetchPackageSizeSuggestion($canonicalProductId: String!) {
    canonicalproduct(input: { id: $canonicalProductId }) {
      _id
      packageSizeSuggestion {
        _id
        length {
          unit
          value
        }
        width {
          unit
          value
        }
        height {
          unit
          value
        }
        weight {
          unit
          value
        }
      }
    }
  },
`);
